/**
 * Tier Ranking และ Icon System
 * จัดการการเรียงลำดับและแสดง icon ของ Tier ต่างๆ
 */

// Tier Ranking Configuration
export const tierRanking = {
  customer: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'ระดับเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'ระดับพื้นฐาน' },
    silver: { rank: 3, icon: '🥇', name: 'Silver', color: 'bg-gray-100 text-gray-800', description: 'ระดับเงิน' },
    gold: { rank: 4, icon: '👑', name: 'Gold', color: 'bg-yellow-100 text-yellow-800', description: 'ระดับทอง' },
    platinum: { rank: 5, icon: '💎', name: 'Platinum', color: 'bg-purple-100 text-purple-800', description: 'ระดับสูงสุด' }
  },
  girl: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'ระดับเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'ระดับพื้นฐาน' },
    premium: { rank: 3, icon: '🥇', name: 'Premium', color: 'bg-green-100 text-green-800', description: 'ระดับพรีเมียม' },
    vip: { rank: 4, icon: '💎', name: 'VIP', color: 'bg-purple-100 text-purple-800', description: 'ระดับ VIP' }
  },
  momo: {
    starter: { rank: 1, icon: '🥉', name: 'Starter', color: 'bg-gray-100 text-gray-800', description: 'ระดับเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'ระดับพื้นฐาน' },
    pro: { rank: 3, icon: '🥇', name: 'Pro', color: 'bg-green-100 text-green-800', description: 'ระดับมืออาชีพ' },
    enterprise: { rank: 4, icon: '👑', name: 'Enterprise', color: 'bg-purple-100 text-purple-800', description: 'ระดับองค์กร' },
    ultra: { rank: 5, icon: '💎', name: 'Ultra Enterprise', color: 'bg-red-100 text-red-800', description: 'ระดับสูงสุด' }
  }
};

// Tier Rank Icons และ Colors
export const tierRankIcons = {
  1: { icon: '🥉', color: 'text-gray-600', name: 'Bronze' },
  2: { icon: '🥈', color: 'text-gray-500', name: 'Silver' },
  3: { icon: '🥇', color: 'text-yellow-500', name: 'Gold' },
  4: { icon: '👑', color: 'text-purple-500', name: 'Crown' },
  5: { icon: '💎', color: 'text-blue-500', name: 'Diamond' }
};

/**
 * ดึงข้อมูล ranking ของ tier
 * @param {string} role - role ของผู้ใช้
 * @param {string} tierKey - key ของ tier
 * @returns {Object} ข้อมูล ranking ของ tier
 */
export const getTierRanking = (role, tierKey) => {
  return tierRanking[role]?.[tierKey] || {
    rank: 0,
    icon: '❓',
    name: tierKey || 'Unknown',
    color: 'bg-gray-100 text-gray-800',
    description: 'ไม่ทราบระดับ'
  };
};

/**
 * ดึงข้อมูล ranking ของ tier ทั้งหมดสำหรับ role
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} รายการ tier เรียงตาม rank
 */
export const getTierRankingsByRole = (role) => {
  const tiers = tierRanking[role] || {};
  return Object.entries(tiers)
    .map(([tierKey, tierData]) => ({
      tierKey,
      ...tierData
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * ดึงข้อมูล tier ทั้งหมดเรียงตาม rank
 * @param {Object} tierConfigs - tier configurations จากฐานข้อมูล
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} รายการ tier เรียงตาม rank
 */
export const getSortedTiersByRank = (tierConfigs, role) => {
  const tiers = tierConfigs[role] || {};
  const rankings = tierRanking[role] || {};
  
  return Object.entries(tiers)
    .map(([tierKey, tierData]) => ({
      tierKey,
      ...tierData,
      ...rankings[tierKey]
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * ดึงข้อมูล tier สำหรับ dropdown เรียงตาม rank
 * @param {Object} tierConfigs - tier configurations จากฐานข้อมูล
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} รายการ tier สำหรับ dropdown
 */
export const getTierDropdownOptions = (tierConfigs, role) => {
  const tiers = tierConfigs[role] || {};
  const rankings = tierRanking[role] || {};
  
  return Object.entries(tiers)
    .map(([tierKey, tierData]) => ({
      value: tierKey,
      label: `${rankings[tierKey]?.icon || '❓'} ${tierData.name}`,
      tierKey,
      tierData,
      ranking: rankings[tierKey]
    }))
    .sort((a, b) => a.ranking.rank - b.ranking.rank);
};

/**
 * สร้าง tier badge พร้อม ranking
 * @param {string} role - role ของผู้ใช้
 * @param {string} tierKey - key ของ tier
 * @param {Object} tierData - ข้อมูล tier จากฐานข้อมูล
 * @param {string} size - ขนาดของ badge (sm, md, lg)
 * @returns {JSX.Element} tier badge component
 */
export const createTierBadge = (role, tierKey, tierData, size = 'md') => {
  const ranking = getTierRanking(role, tierKey);
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-base'
  };
  
  return {
    icon: ranking.icon,
    name: tierData?.name || ranking.name,
    color: tierData?.color || ranking.color,
    sizeClass: sizeClasses[size],
    description: ranking.description,
    rank: ranking.rank
  };
};

/**
 * สร้าง tier comparison table
 * @param {string} role - role ของผู้ใช้
 * @param {Object} tierConfigs - tier configurations จากฐานข้อมูล
 * @returns {Array} รายการ tier สำหรับ comparison table
 */
export const createTierComparison = (role, tierConfigs) => {
  const tiers = tierConfigs[role] || {};
  const rankings = tierRanking[role] || {};
  
  return Object.entries(tiers)
    .map(([tierKey, tierData]) => ({
      tierKey,
      ...tierData,
      ...rankings[tierKey],
      features: tierData.features || [],
      price: tierData.price || 0,
      maxImages: tierData.maxImages || 0,
      dailyViews: tierData.dailyViews || 0,
      monthlyBookings: tierData.monthlyBookings || 0,
      revenueShare: tierData.revenueShare || 0
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * ดึงข้อมูล tier ที่แนะนำสำหรับผู้ใช้ใหม่
 * @param {string} role - role ของผู้ใช้
 * @returns {Object} tier ที่แนะนำ
 */
export const getRecommendedTier = (role) => {
  const rankings = tierRanking[role] || {};
  
  // หา tier ที่มี rank = 2 (Basic level)
  const recommendedTier = Object.entries(rankings).find(([_, tierData]) => tierData.rank === 2);
  
  if (recommendedTier) {
    return {
      tierKey: recommendedTier[0],
      ...recommendedTier[1]
    };
  }
  
  // ถ้าไม่มี Basic level ให้หา tier ที่มี rank ต่ำสุด
  const lowestRankTier = Object.entries(rankings).reduce((min, [tierKey, tierData]) => {
    return tierData.rank < min.rank ? { tierKey, ...tierData } : min;
  }, { rank: Infinity });
  
  return lowestRankTier.rank !== Infinity ? lowestRankTier : null;
};

/**
 * ตรวจสอบว่า tier หนึ่งสูงกว่า tier อื่นหรือไม่
 * @param {string} role - role ของผู้ใช้
 * @param {string} tier1 - tier แรก
 * @param {string} tier2 - tier ที่สอง
 * @returns {boolean} true ถ้า tier1 สูงกว่า tier2
 */
export const isTierHigher = (role, tier1, tier2) => {
  const ranking1 = getTierRanking(role, tier1);
  const ranking2 = getTierRanking(role, tier2);
  
  return ranking1.rank > ranking2.rank;
};

/**
 * ดึงข้อมูล tier ที่สูงกว่าปัจจุบัน
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentTier - tier ปัจจุบัน
 * @param {Object} tierConfigs - tier configurations จากฐานข้อมูล
 * @returns {Array} รายการ tier ที่สูงกว่า
 */
export const getHigherTiers = (role, currentTier, tierConfigs) => {
  const currentRanking = getTierRanking(role, currentTier);
  const tiers = tierConfigs[role] || {};
  
  return Object.entries(tiers)
    .filter(([tierKey, _]) => {
      const ranking = getTierRanking(role, tierKey);
      return ranking.rank > currentRanking.rank;
    })
    .map(([tierKey, tierData]) => ({
      tierKey,
      ...tierData,
      ...getTierRanking(role, tierKey)
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * สร้าง tier progress bar
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentTier - tier ปัจจุบัน
 * @returns {Object} ข้อมูลสำหรับ progress bar
 */
export const createTierProgress = (role, currentTier) => {
  const rankings = tierRanking[role] || {};
  const tiers = Object.entries(rankings);
  const currentRanking = getTierRanking(role, currentTier);
  
  const maxRank = Math.max(...tiers.map(([_, tierData]) => tierData.rank));
  const progress = (currentRanking.rank / maxRank) * 100;
  
  return {
    currentRank: currentRanking.rank,
    maxRank,
    progress,
    currentTier: currentRanking.name,
    nextTier: tiers.find(([_, tierData]) => tierData.rank === currentRanking.rank + 1)?.[1]?.name || null
  };
};









